import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, ArrowRight, Link2, Pencil, Plus } from "lucide-react";

import {
  AnalysisSection,
  MatchingSummary,
  MergeReport,
  PlanOperation,
  useDocumentAnalysisAPI,
} from "../DocumentAnalysisAPI";
import { classLabel, fieldLabel } from "../administration/document-analysis/ontologyValidation";

import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

/**
 * The last thing between a document and the estate.
 *
 * Everything before this point could be undone by ignoring it: the analysis stages its objects, the
 * matcher writes a plan, and neither touches anything real. Pressing the button here does. So the
 * dialog's job is not to announce that something is about to happen — it is to make the plan
 * readable enough that a yes means something.
 *
 * It is read as a whole and confirmed as a whole. Picking operations one by one was considered and
 * dropped: a reference whose object you deselected has nothing left to point at, and a dialog that
 * has to explain that is no longer a dialog you read before saying yes.
 *
 * **Marked, not withheld.** A creation the matcher flagged for review — a new object hanging between
 * one settled and one unsettled anchor — is applied with the rest, and shown with a reason. Holding
 * it back would leave the merge permanently half done; hiding the flag would make the confirmation
 * a formality. Neither is what a person needs in order to decide.
 *
 * The open questions are listed and not applied. That is not a policy: an unresolved match has no
 * object to write to, which is precisely what makes it unresolved. It stays a question, answerable
 * later in the decision dialog.
 */

export interface MergeSubject {
  analysisId: string;
  documentName: string;
  summary?: MatchingSummary | null;
}

/** Ein Wert, wie er in einer Zeile stehen kann — kurz gehalten, nie über die Breite hinaus. */
const shortly = (value: any): string => {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "object") return JSON.stringify(value);

  return String(value);
};

/** Ein Objekt in einer Zeile der Tabelle: Klasse und die ersten Werte, die es trägt. */
const describeObject = (className: string | undefined, values: Record<string, any>, locale: string): string => {
  const shown = Object.entries(values ?? {})
    .filter(([, value]) => value !== null && value !== undefined && value !== "" && typeof value !== "object")
    .slice(0, 3)
    .map(([, value]) => shortly(value));

  return `${classLabel(className, locale)}${shown.length ? `: ${shown.join(", ")}` : ""}`;
};

/** So viele Zeilen zeigt die Tabelle; der Rest wird gezählt. Übernommen werden alle. */
const ROW_LIMIT = 300;

export default function MergeDialog({
  subject,
  onClose,
  onMerge,
}: {
  subject: MergeSubject | null;
  onClose: () => void;
  /** Löst auf, wenn angewendet wurde. Der Bericht wird danach hier gezeigt. */
  onMerge: (analysisId: string) => Promise<MergeReport>;
}) {
  const { t, i18n } = useTranslation();
  const api = useDocumentAnalysisAPI();
  const locale = i18n.language ?? "de";

  const [operations, setOperations] = useState<PlanOperation[] | null>(null);
  /** Die abgelegten Sätze — nur wegen der offenen Katalogfragen und ihrer Kandidaten. */
  const [staged, setStaged] = useState<any[]>([]);
  const [choosing, setChoosing] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [report, setReport] = useState<MergeReport | null>(null);
  /** Bei einem Bündel: seine Abschnitte, wie der Lauf sie gelesen hat. */
  const [sections, setSections] = useState<AnalysisSection[]>([]);

  const analysisId = subject?.analysisId ?? null;

  /*
   * Der Plan wird beim Öffnen geholt, nicht mit der Zeile mitgeliefert.
   *
   * Eine Liste von hundert Dateien trüge sonst hundert Operationssätze mit sich, damit an einer
   * davon ein Knopf erscheinen kann. Die Zeile weiss, *dass* etwas ansteht; *was* ansteht, wird
   * gelesen, wenn jemand hinsieht.
   */
  useEffect(() => {
    if (!analysisId) {
      setOperations(null);
      setReport(null);
      setError(null);

      return;
    }

    const abbruch = new AbortController();

    setLoading(true);
    setError(null);
    setReport(null);

    Promise.allSettled([api.getAnalysis(analysisId), api.getResolution(analysisId)])
      .then(([analysisResult, resolutionResult]) => {
        if (abbruch.signal.aborted) return;

        if (analysisResult.status === "rejected") {
          setError(analysisResult.reason?.message ?? String(analysisResult.reason));
          return;
        }

        const analysis = analysisResult.value;
        const resolution = resolutionResult.status === "fulfilled" ? resolutionResult.value : null;

        setOperations(analysis?.analysis?.matchingPlan?.operations ?? []);
        setSections(analysis?.analysis?.sections ?? []);
        if (resolution) {
          setStaged(resolution.extractedObjects ?? []);
        }
      })
      .catch((reason: any) => {
        if (abbruch.signal.aborted) return;

        setError(reason?.message ?? String(reason));
      })
      .finally(() => {
        if (!abbruch.signal.aborted) setLoading(false);
      });

    return () => abbruch.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analysisId]);

  /** Neu lesen, weil die Antwort den Plan verändert hat — und mit ihm die Sperre. */
  const reload = useCallback(async () => {
    if (!analysisId) return;

    const [analysisResult, resolutionResult] = await Promise.allSettled([
      api.getAnalysis(analysisId),
      api.getResolution(analysisId),
    ]);

    if (analysisResult.status === "rejected") {
      throw analysisResult.reason;
    }

    const analysis = analysisResult.value;
    const resolution = resolutionResult.status === "fulfilled" ? resolutionResult.value : null;

    setOperations(analysis?.analysis?.matchingPlan?.operations ?? []);
    setSections(analysis?.analysis?.sections ?? []);
    if (resolution) {
      setStaged(resolution.extractedObjects ?? []);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analysisId]);

  const choose = useCallback(
    async (extractedObjectId: string, field: string, value: string) => {
      if (!analysisId) return;

      setChoosing(`${extractedObjectId}:${field}`);
      setError(null);

      try {
        await api.chooseVocabulary(analysisId, { extractedObjectId, field, value });
        await reload();
      } catch (reason: any) {
        setError(reason?.message ?? String(reason));
      } finally {
        setChoosing(null);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [analysisId, reload],
  );

  const confirm = useCallback(async () => {
    if (!analysisId) return;

    setBusy(true);
    setError(null);

    try {
      setReport(await onMerge(analysisId));
    } catch (reason: any) {
      setError(reason?.message ?? String(reason));
    } finally {
      setBusy(false);
    }
  }, [analysisId, onMerge]);

  if (!subject) return null;

  const updates = (operations ?? []).filter((operation) => operation.op === "updateAttributes");
  const creates = (operations ?? []).filter((operation) => operation.op === "createObject");
  const references = (operations ?? []).filter((operation) => operation.op === "createReference");
  const confirmations = (operations ?? []).filter((operation) => operation.op === "confirmReference");
  const open = (operations ?? []).filter((operation) => operation.op === "unresolved");

  const nothingToDo =
    !loading && operations !== null && updates.length + creates.length + references.length === 0;

  /*
   * Neuanlagen, denen etwas Verpflichtendes fehlen würde.
   *
   * Sie sperren den **ganzen** Plan, nicht nur sich selbst. Das klingt streng und ist die schonendere
   * Regel: Ein Plan wird genau einmal angewendet, und eine übersprungene Neuanlage wäre endgültig
   * weg — ausgerechnet die, von der das Modell sagt, dass ihr etwas Wesentliches fehlt. Wird nichts
   * geschrieben, lässt sich die offene Frage beantworten und danach übernehmen.
   */
  const blocked = creates.filter((operation) => (operation.incomplete?.length ?? 0) > 0);

  /*
   * Eine Liste wird als Tabelle gezeigt: eine Zeile je Datenzeile, darin was aus ihr neu entsteht
   * oder sich ändert. Dreihundert Karten untereinander liest niemand, bevor er „Übernehmen" drückt.
   */
  const rowNumbers = Array.from(
    new Set([...creates, ...updates].flatMap((operation) => operation.rows ?? [])),
  ).sort((left, right) => left - right);
  const isList = rowNumbers.length > 0;
  const byRow = new Map<number, PlanOperation[]>();
  [...creates, ...updates].forEach((operation) =>
    (operation.rows ?? []).forEach((row) => byRow.set(row, [...(byRow.get(row) ?? []), operation])),
  );
  const listCreates = isList ? creates.filter((operation) => !operation.rows?.length) : creates;
  const listUpdates = isList ? updates.filter((operation) => !operation.rows?.length) : updates;

  /** Bei einem Bündel: aus welchem Abschnitt eine Operation kommt — klein neben der Klasse. */
  const sectionBadge = (operation: PlanOperation) =>
    sections.length > 1 && operation.section ? (
      <span className="rounded-sm bg-muted px-1.5 py-0.5 text-xs font-normal text-muted-foreground">
        {t("portfolioDashboard.merge.sectionBadge", { number: operation.section })}
      </span>
    ) : null;

  /**
   * Die offenen Katalogfragen, nach dem Satz gesucht, aus dem die Neuanlage entstand.
   *
   * Sie stehen am abgelegten Satz und nicht am Plan: Der Plan sagt, **was** fehlt; wie man es
   * beantwortet, ist eine andere Auskunft, und sie hängt an der Stelle des Dokuments, aus der die
   * Frage kommt.
   */
  const questionsFor = (extractedObjectId?: string) => {
    if (!extractedObjectId) return [];

    const row = staged.find((entry: any) => String(entry?._id) === String(extractedObjectId));

    return ((row?.vocabulary ?? []) as any[]).filter(
      (entry) => entry?.outcome === "ambiguous" && (entry?.candidates?.length ?? 0) > 0,
    );
  };

  return (
    <Dialog open onOpenChange={(next) => (next ? undefined : onClose())}>
      <DialogContent className="max-w-3xl">
        {/*
          * Überschrift und Einleitung wechseln mit dem Bericht die Zeitform.
          *
          * Vorher ist der Dialog eine Frage: was geschähe, und dass bis hierher nichts geschrieben
          * wurde. Danach ist er eine Auskunft. Beides gleichzeitig zu behaupten — „nichts
          * geschrieben" über „5 angelegt" — ist nicht bloss unschön, sondern eine falsche Aussage
          * an der einzigen Stelle, an der dieses Produkt in den Bestand schreibt.
          */}
        <DialogHeader>
          <DialogTitle>
            {t(report ? "portfolioDashboard.merge.titleDone" : "portfolioDashboard.merge.title")}
          </DialogTitle>
        </DialogHeader>

        <p className="text-sm text-muted-foreground">
          {t(report ? "portfolioDashboard.merge.leadDone" : "portfolioDashboard.merge.lead", {
            document: subject.documentName,
          })}
        </p>

        {loading ? (
          <p className="py-6 text-center text-sm text-muted-foreground">
            {t("portfolioDashboard.merge.loading")}
          </p>
        ) : null}

        {error ? (
          <p className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
            {error}
          </p>
        ) : null}

        {/* Nach dem Anwenden steht hier, was geschehen ist — dieselbe Fläche, anderer Inhalt. Den
            Dialog beim Erfolg sofort zu schliessen hiesse, das Ergebnis wegzuwerfen. */}
        {report ? (
          <div className="space-y-2 text-sm">
            <p className="font-medium">
              {t("portfolioDashboard.merge.done", {
                updated: report.updated,
                created: report.created,
                referenced: report.referenced,
              })}
            </p>
            {report.confirmed > 0 ? (
              <p className="text-muted-foreground">
                {t("portfolioDashboard.merge.confirmed", { count: report.confirmed })}
              </p>
            ) : null}
            {report.skipped > 0 ? (
              <p className="text-muted-foreground">
                {t("portfolioDashboard.merge.skipped", { count: report.skipped })}
              </p>
            ) : null}
            {report.failed > 0 ? (
              <div className="space-y-1 text-destructive">
                <p>{t("portfolioDashboard.merge.failed", { count: report.failed })}</p>
                <ul className="list-inside list-disc">
                  {report.operations
                    .filter((operation) => operation.outcome === "failed")
                    .map((operation, index) => (
                      <li key={index}>
                        {classLabel(operation.className, locale)} — {operation.reason}
                      </li>
                    ))}
                </ul>
              </div>
            ) : null}
          </div>
        ) : null}

        {/*
          * Die Sperre steht oben, nicht bei der betroffenen Zeile.
          *
          * Sie beantwortet die Frage, mit der jemand den Dialog öffnet — „kann ich das übernehmen?"
          * —, und die Antwort ist nein. Zwischen den Operationen versteckt käme sie erst zur
          * Kenntnis, wenn der Knopf schon nicht reagiert.
          */}
        {!loading && !report && blocked.length > 0 ? (
          <div className="space-y-1 rounded-md border border-amber-500/40 bg-amber-50 p-3 text-sm dark:bg-amber-950/40">
            <p className="font-medium text-amber-900 dark:text-amber-200">
              {t("portfolioDashboard.merge.blocked", { count: blocked.length })}
            </p>
            <ul className="space-y-2 text-xs text-amber-800 dark:text-amber-300">
              {blocked.map((operation, index) => {
                const questions = questionsFor(operation.extractedObjectId);

                return (
                  <li key={index}>
                    <span className="font-medium">{classLabel(operation.className, locale)}</span> —{" "}
                    {(operation.incomplete ?? []).map((entry) => entry.label).join(", ")}

                    {/* Die Frage steht dort, wo die Sperre steht. Sie anderswo zu beantworten hiesse,
                        den Dialog zu schliessen, etwas zu suchen und wiederzukommen — für eine
                        Entscheidung zwischen zwei Zeilen. */}
                    {questions.map((question: any) => (
                      <div key={question.field} className="mt-1.5 space-y-1">
                        <div className="text-amber-900 dark:text-amber-200">
                          {t("portfolioDashboard.merge.asStated", { words: question.words })}
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {question.candidates.map((candidate: any) => (
                            <Button
                              key={candidate.value}
                              type="button"
                              variant="outline"
                              size="sm"
                              className="h-7 px-2 text-xs"
                              disabled={choosing !== null}
                              onClick={() =>
                                choose(operation.extractedObjectId!, question.field, candidate.value)
                              }
                            >
                              {candidate.label} · {candidate.value}
                            </Button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </li>
                );
              })}
            </ul>
            <p className="text-xs text-amber-800 dark:text-amber-300">
              {t("portfolioDashboard.merge.blockedHint")}
            </p>
          </div>
        ) : null}

        {!loading && !report && operations !== null ? (
          <div className="max-h-[50vh] space-y-4 overflow-y-auto pr-1">
            {nothingToDo ? (
              <p className="text-sm text-muted-foreground">
                {t("portfolioDashboard.merge.nothing")}
              </p>
            ) : null}

            {sections.length > 0 ? (
              <section className="space-y-1">
                <h4 className="text-sm font-semibold">
                  {t("portfolioDashboard.merge.bundle", { count: sections.length })}
                </h4>
                <ol className="space-y-0.5 text-xs text-muted-foreground">
                  {sections.map((section) => (
                    <li key={section.number} className="flex flex-wrap gap-x-2">
                      <span className="text-foreground">
                        {section.number}. {section.title}
                      </span>
                      <span>{t("portfolioDashboard.merge.pages", { from: section.pageFrom, to: section.pageTo })}</span>
                      <span>
                        {section.roleName
                          ? `${section.roleName}${section.subtypeName ? ` / ${section.subtypeName}` : ""}`
                          : t("portfolioDashboard.merge.roleUndecided")}
                      </span>
                      <span>{t("portfolioDashboard.merge.objects", { count: section.objectCount ?? 0 })}</span>
                    </li>
                  ))}
                </ol>
              </section>
            ) : null}

            {isList ? (
              <section className="space-y-1">
                <h4 className="flex items-center gap-1.5 text-sm font-semibold">
                  <Plus className="h-3.5 w-3.5" aria-hidden />
                  {t("portfolioDashboard.merge.rows", { count: rowNumbers.length })}
                </h4>
                <div className="overflow-x-auto rounded-md border">
                  <table className="w-full text-xs">
                    <thead className="bg-muted/50 text-left text-muted-foreground">
                      <tr>
                        <th className="px-2 py-1 font-medium">{t("portfolioDashboard.merge.row")}</th>
                        <th className="px-2 py-1 font-medium">{t("portfolioDashboard.merge.rowObjects")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rowNumbers.slice(0, ROW_LIMIT).map((row) => (
                        <tr key={row} className="border-t align-top">
                          <td className="px-2 py-1 tabular-nums text-muted-foreground">{row}</td>
                          <td className="px-2 py-1">
                            {(byRow.get(row) ?? []).map((operation, index) => (
                              <div key={index} className="flex flex-wrap items-center gap-1.5">
                                <span>
                                  {operation.op === "createObject"
                                    ? describeObject(operation.className, operation.attributes ?? {}, locale)
                                    : `${classLabel(operation.target?.className, locale)}: ${(operation.changes ?? [])
                                        .map((change) => `${fieldLabel(operation.target?.className, change.field, locale)} ${shortly(change.new)}`)
                                        .join(", ")}`}
                                </span>
                                <span className="text-muted-foreground">
                                  {operation.op === "createObject"
                                    ? t("portfolioDashboard.merge.new")
                                    : t("portfolioDashboard.merge.changed")}
                                </span>
                                {operation.requiresReview ? (
                                  <AlertTriangle className="h-3 w-3 text-amber-600" aria-label={t("portfolioDashboard.merge.review")} />
                                ) : null}
                              </div>
                            ))}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {rowNumbers.length > ROW_LIMIT ? (
                  <p className="text-xs text-muted-foreground">
                    {t("portfolioDashboard.merge.rowsMore", { count: rowNumbers.length - ROW_LIMIT })}
                  </p>
                ) : null}
                {listCreates.length + listUpdates.length > 0 ? (
                  <p className="pt-1 text-xs font-medium">{t("portfolioDashboard.merge.wholeList")}</p>
                ) : null}
              </section>
            ) : null}

            {listUpdates.length > 0 ? (
              <section className="space-y-1">
                <h4 className="flex items-center gap-1.5 text-sm font-semibold">
                  <Pencil className="h-3.5 w-3.5" aria-hidden />
                  {t("portfolioDashboard.merge.updates", { count: listUpdates.length })}
                </h4>
                <ul className="space-y-2 text-sm">
                  {listUpdates.map((operation, index) => (
                    <li key={index} className="rounded-md border p-2">
                      <div className="flex items-center gap-2 font-medium">
                        {classLabel(operation.target?.className, locale)}
                        {sectionBadge(operation)}
                      </div>
                      <ul className="mt-1 space-y-0.5 text-xs text-muted-foreground">
                        {(operation.changes ?? []).map((change, position) => (
                          <li key={position} className="flex flex-wrap items-center gap-1">
                            <span className="text-foreground">
                              {fieldLabel(operation.target?.className, change.field, locale)}
                            </span>
                            <span>{shortly(change.old)}</span>
                            <ArrowRight className="h-3 w-3" aria-hidden />
                            <span className="text-foreground">{shortly(change.new)}</span>
                          </li>
                        ))}
                      </ul>
                    </li>
                  ))}
                </ul>
              </section>
            ) : null}

            {listCreates.length > 0 ? (
              <section className="space-y-1">
                <h4 className="flex items-center gap-1.5 text-sm font-semibold">
                  <Plus className="h-3.5 w-3.5" aria-hidden />
                  {t("portfolioDashboard.merge.creates", { count: listCreates.length })}
                </h4>
                <ul className="space-y-2 text-sm">
                  {listCreates.map((operation, index) => (
                    <li key={index} className="rounded-md border p-2">
                      <div className="flex items-center gap-2 font-medium">
                        {classLabel(operation.className, locale)}
                        {sectionBadge(operation)}
                        {operation.requiresReview ? (
                          <span className="inline-flex items-center gap-1 rounded-sm bg-amber-100 px-1.5 py-0.5 text-xs font-normal text-amber-800 dark:bg-amber-950 dark:text-amber-200">
                            <AlertTriangle className="h-3 w-3" aria-hidden />
                            {t("portfolioDashboard.merge.review")}
                          </span>
                        ) : null}
                      </div>
                      <p className="mt-1 text-xs text-muted-foreground">
                        {Object.entries(operation.attributes ?? {})
                          .filter(([, value]) => value !== null && value !== undefined && value !== "")
                          .slice(0, 6)
                          .map(([field, value]) => `${fieldLabel(operation.className, field, locale)}: ${shortly(value)}`)
                          .join(" · ") || t("portfolioDashboard.merge.noAttributes")}
                      </p>
                    </li>
                  ))}
                </ul>
              </section>
            ) : null}

            {references.length > 0 ? (
              <section className="space-y-1">
                <h4 className="flex items-center gap-1.5 text-sm font-semibold">
                  <Link2 className="h-3.5 w-3.5" aria-hidden />
                  {t("portfolioDashboard.merge.references", { count: references.length })}
                </h4>
                <ul className="space-y-1 text-xs text-muted-foreground">
                  {references.map((operation, index) => (
                    <li key={index}>
                      {classLabel(operation.source?.className, locale)}
                      {" · "}
                      {fieldLabel(operation.source?.className, operation.name ?? "", locale)}
                      {" → "}
                      {classLabel(operation.target?.className, locale)}
                    </li>
                  ))}
                </ul>
              </section>
            ) : null}

            {/* Zwei Zeilen, keine Listen: Bestätigte Kanten schreiben nichts, offene Fragen können
                nicht angewendet werden. Beides gehört in den Blick, keines in den Vordergrund. */}
            {confirmations.length > 0 ? (
              <p className="text-xs text-muted-foreground">
                {t("portfolioDashboard.merge.corroborated", { count: confirmations.length })}
              </p>
            ) : null}

            {open.length > 0 ? (
              <p className="text-xs text-amber-700 dark:text-amber-300">
                {t("portfolioDashboard.merge.open", { count: open.length })}
              </p>
            ) : null}
          </div>
        ) : null}

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose} disabled={busy}>
            {report ? t("global.close") : t("global.cancel")}
          </Button>
          {!report ? (
            <Button
              onClick={confirm}
              disabled={busy || loading || nothingToDo || blocked.length > 0 || operations === null}
            >
              {busy ? t("portfolioDashboard.merge.applying") : t("portfolioDashboard.merge.apply")}
            </Button>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}
